import { FC, lazy } from 'react';
import {
  NwpCapacityGraphParams,
  ShipperTransactionsParams,
} from 'src/apis/capacity-management/types';
import { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useNwpCapacityGraph } from 'src/apis/capacity-management/use-nwp-capacity-graph';
import { useShipperTransactions } from 'src/apis/capacity-management/use-shipper-transactions';
import { NetworkPoint } from 'src/apis/monolith/types';
import { PageSpinner } from 'src/components/spinner-container';
import { usePageParams } from 'src/pages/transport/network-points/details/capacity-overview/use-page-params';
import { NetworkPointRequest } from 'src/pages/transport/network-points/details/use-network-point';
import { useMemoOne } from 'use-memo-one';

const Page = lazy(() => import('./page'));

type ContentProps = {
  networkPoint: NetworkPoint;
  capacityManagementRoot: CapacityManagementRootRequest;
};

const Content: FC<ContentProps> = ({
  networkPoint,
  capacityManagementRoot,
}) => {
  const pageParams = usePageParams();

  const transactionsParams = useMemoOne<ShipperTransactionsParams>(() => {
    const {
      start,
      pageSize,
      sortBy,
      sortDirection,
      runtimeIntersectionFilterStart,
      runtimeIntersectionFilterEnd,
    } = pageParams.value;

    return {
      offset: start,
      limit: pageSize,
      sortBy,
      sortDirection,
      status: ['SUCCESSFUL'],
      networkPointId: [networkPoint.networkPointUuid],
      runtimeIntersectionFilterStart,
      runtimeIntersectionFilterEnd,
    };
  }, [pageParams.value, networkPoint.networkPointUuid]);

  const shipperTransactions = useShipperTransactions({
    capacityManagementRoot,
    params: transactionsParams,
  });

  const paramsForGraph = useMemoOne<NwpCapacityGraphParams>(() => {
    return {
      runtimeIntersectionFilterStart:
        pageParams.value.runtimeIntersectionFilterStart,
      runtimeIntersectionFilterEnd:
        pageParams.value.runtimeIntersectionFilterEnd,
      granularity: pageParams.value.granularity,
      nwpId: networkPoint.networkPointUuid,
    };
  }, [pageParams.value, networkPoint.networkPointUuid]);

  const nwpCapacityGraph = useNwpCapacityGraph({
    capacityManagementRoot,
    params: paramsForGraph,
  });

  return (
    <Page
      capacityManagementRoot={capacityManagementRoot}
      networkPoint={networkPoint}
      shipperTransactions={shipperTransactions}
      pageParams={pageParams}
      nwpCapacityGraph={nwpCapacityGraph}
    />
  );
};

type Props = {
  networkPoint: NetworkPointRequest;
  capacityManagementRoot: CapacityManagementRootRequest;
};

export const CapacityOverview: FC<Props> = ({
  networkPoint,
  capacityManagementRoot,
}) => {
  if (!networkPoint.response) return <PageSpinner />;

  return (
    <Content
      networkPoint={networkPoint.response.data}
      capacityManagementRoot={capacityManagementRoot}
    />
  );
};
